import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { FaTag, FaRupeeSign, FaInfoCircle, FaMagic } from "react-icons/fa";
import { toast } from "react-toastify";
import StudentService from "../../services/studentService";

const DiscountModal = ({ show, onHide, studentId, studentName, courses, onSuccess }) => {
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Debug log
  useEffect(() => {
    if (show) {
      console.log("DiscountModal - Courses:", courses);
    }
  }, [show, courses]);

  // Auto-generate discount reason when discount amount changes
  useEffect(() => {
    if (selectedCourseId && discountAmount && parseFloat(discountAmount) > 0) {
      const selectedCourse = courses.find(c => {
        const courseId = c.course_id || c.id;
        return courseId === parseInt(selectedCourseId);
      });
      
      if (selectedCourse) {
        const totalFee = selectedCourse.total_fees || 0;
        const discountAmt = parseFloat(discountAmount);
        const discountPercent = (discountAmt / totalFee) * 100;
        const courseName = selectedCourse.course_name || selectedCourse.name || "this course";
        
        // Auto-generate reason based on discount percentage
        let autoReason = "";
        
        if (discountPercent >= 100) {
          autoReason = `Full scholarship - 100% discount on ${courseName}`;
        } else if (discountPercent >= 75) {
          autoReason = `Special scholarship - ${Math.round(discountPercent)}% discount on ${courseName}`;
        } else if (discountPercent >= 50) {
          autoReason = `50% discount offer on ${courseName}`;
        } else if (discountPercent >= 25) {
          autoReason = `Special discount of ${Math.round(discountPercent)}% on ${courseName}`;
        } else if (discountPercent >= 10) {
          autoReason = `Discount of ₹${discountAmt.toLocaleString()} (${Math.round(discountPercent)}%) on ${courseName}`;
        } else {
          autoReason = `Discount of ₹${discountAmt.toLocaleString()} applied on ${courseName}`;
        }
        
        setDiscountReason(autoReason);
      }
    } else if (!discountAmount) {
      setDiscountReason("");
    }
  }, [discountAmount, selectedCourseId, courses]);

  // Get selected course - handle both possible key names
  const selectedCourse = courses.find(c => {
    const courseId = c.course_id || c.id;
    return courseId === parseInt(selectedCourseId);
  });
  
  const originalFee = selectedCourse ? (selectedCourse.total_fees || 0) : 0;
  const paidAmount = selectedCourse ? (selectedCourse.paid_amount || 0) : 0;
  const existingDiscount = selectedCourse ? (selectedCourse.discount_amount || 0) : 0;
  const discountedFee = originalFee - (parseFloat(discountAmount) || 0);
  const finalDue = discountedFee - paidAmount;
  
  // Calculate discount percentage
  const discountPercent = originalFee > 0 && discountAmount 
    ? (parseFloat(discountAmount) / originalFee) * 100 
    : 0;

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedCourseId) {
      newErrors.course = "Please select a course";
    }
    
    if (!discountAmount || parseFloat(discountAmount) <= 0) {
      newErrors.amount = "Please enter a valid discount amount";
    } else if (parseFloat(discountAmount) > originalFee) {
      newErrors.amount = `Discount cannot exceed total fees (₹${originalFee.toLocaleString()})`;
    } else if (existingDiscount > 0) {
      newErrors.amount = `Discount already applied (₹${existingDiscount}). Cannot apply another discount.`;
    }
    
    if (!discountReason.trim()) {
      newErrors.reason = "Please enter a reason for discount";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const response = await StudentService.applyDiscount(studentId, {
        course_id: parseInt(selectedCourseId),
        discount_amount: parseFloat(discountAmount),
        discount_reason: discountReason.trim()
      });
      
      if (response.status) {
        toast.success(`Discount of ₹${parseFloat(discountAmount).toLocaleString()} applied successfully!`);
        
        setSelectedCourseId("");
        setDiscountAmount("");
        setDiscountReason("");
        
        onSuccess();
        onHide();
      }
    } catch (error) {
      console.error("Discount error:", error);
      const errorMsg = error.response?.data?.message || "Failed to apply discount";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCourseId("");
    setDiscountAmount("");
    setDiscountReason("");
    setErrors({});
    onHide();
  };

  // Helper to get course display name
  const getCourseDisplay = (course) => {
    const courseName = course.course_name || course.name || "Unknown Course";
    const totalFee = course.total_fees || 0;
    const paid = course.paid_amount || 0;
    const due = course.due_amount || (totalFee - paid);
    const discount = course.discount_amount || 0;
    
    let statusText = "";
    if (discount > 0) {
      statusText = ` [Discount Applied: ₹${discount.toLocaleString()}]`;
    } else if (due <= 0) {
      statusText = ` [Fully Paid]`;
    }
    
    return `${courseName} - Total: ₹${totalFee.toLocaleString()} | Paid: ₹${paid.toLocaleString()} | Due: ₹${due.toLocaleString()}${statusText}`;
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="bg-warning">
        <Modal.Title>
          <FaTag className="me-2" />
          Apply Discount - {studentName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="info" className="mb-4">
          <FaInfoCircle className="me-2" />
          <strong>Note:</strong> Discount reason will be auto-generated based on the discount amount.
          You can edit it if needed.
        </Alert>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Select Course *</Form.Label>
            <Form.Select
              value={selectedCourseId}
              onChange={(e) => {
                setSelectedCourseId(e.target.value);
                setErrors({ ...errors, course: null });
              }}
              isInvalid={!!errors.course}
            >
              <option value="">-- Select a course --</option>
              {courses.map((course) => {
                const courseId = course.course_id || course.id;
                const discountApplied = (course.discount_amount || 0) > 0;
                const dueAmount = course.due_amount || (course.total_fees - course.paid_amount);
                const isFullyPaid = dueAmount <= 0;
                
                // Disable if discount already applied or fully paid
                const isDisabled = discountApplied || isFullyPaid;
                
                return (
                  <option 
                    key={courseId} 
                    value={courseId}
                    disabled={isDisabled}
                    style={{ backgroundColor: isDisabled ? '#f5f5f5' : 'white' }}
                  >
                    {getCourseDisplay(course)}
                    {discountApplied && " (Discount Already Applied)"}
                    {isFullyPaid && " (Fully Paid)"}
                  </option>
                );
              })}
            </Form.Select>
            {courses.length === 0 && (
              <Form.Text className="text-danger">
                No courses found for this student.
              </Form.Text>
            )}
            <Form.Control.Feedback type="invalid">
              {errors.course}
            </Form.Control.Feedback>
          </Form.Group>

          {selectedCourse && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Original Course Fee</Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  value={`₹${originalFee.toLocaleString()}`}
                  className="bg-light"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Amount Already Paid</Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  value={`₹${paidAmount.toLocaleString()}`}
                  className="bg-light"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Discount Amount (₹) *</Form.Label>
                <div className="position-relative">
                  <span className="position-absolute top-50 start-0 translate-middle-y ms-3">
                    <FaRupeeSign />
                  </span>
                  <Form.Control
                    type="number"
                    className="ps-5"
                    placeholder="Enter discount amount"
                    value={discountAmount}
                    onChange={(e) => {
                      setDiscountAmount(e.target.value);
                      setErrors({ ...errors, amount: null });
                    }}
                    isInvalid={!!errors.amount}
                  />
                </div>
                <Form.Text className="text-muted">
                  Maximum discount allowed: ₹{originalFee.toLocaleString()}
                  {discountAmount && discountPercent > 0 && (
                    <span className="ms-2 text-success fw-bold">
                      ({Math.round(discountPercent)}% discount)
                    </span>
                  )}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.amount}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Discounted Fee</Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  value={`₹${discountedFee.toLocaleString()}`}
                  className="bg-light text-success fw-bold"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>New Due Amount After Discount</Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  value={`₹${finalDue < 0 ? 0 : finalDue.toLocaleString()}`}
                  className={`bg-light fw-bold ${finalDue > 0 ? 'text-danger' : 'text-success'}`}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Discount Reason *</Form.Label>
                <div className="position-relative">
                  <span className="position-absolute top-50 start-0 translate-middle-y ms-3">
                    <FaMagic className="text-warning" />
                  </span>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    className="ps-5"
                    placeholder="Discount reason will be auto-generated"
                    value={discountReason}
                    onChange={(e) => {
                      setDiscountReason(e.target.value);
                      setErrors({ ...errors, reason: null });
                    }}
                    isInvalid={!!errors.reason}
                  />
                </div>
                <Form.Text className="text-muted text-success">
                  ✨ Reason auto-generated based on discount amount. You can edit if needed.
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.reason}
                </Form.Control.Feedback>
              </Form.Group>

              <Alert variant="warning" className="mt-3">
                <strong>⚠️ Important:</strong> Once discount is applied, it cannot be reversed.
                Make sure the discount amount is correct before proceeding.
              </Alert>
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="warning"
          onClick={handleSubmit}
          disabled={loading || !selectedCourseId || !discountAmount || !discountReason}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Applying Discount...
            </>
          ) : (
            <>
              <FaTag className="me-2" />
              Apply Discount
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DiscountModal;